import React, { useState } from 'react';
import { Box, Typography, Card, CardContent, Stack, Chip, Button, ToggleButtonGroup, ToggleButton, Paper } from '@mui/material';
import { exportChequesToExcel } from '../utils/exportCheques'; // we'll create this
import finance from '../data/finance';
import { motion } from 'framer-motion';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DownloadIcon from '@mui/icons-material/Download';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import PaymentsIcon from '@mui/icons-material/Payments';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const MotionPaper = motion(Paper);
const MotionCard = motion(Card);

const UncollectedChequesPage = () => {
  const [filterStatus, setFilterStatus] = useState('all');

  const cheques = finance.filter((f) => f.method === 'شيك');

  const filteredCheques = cheques.filter((cheque) => {
    if (filterStatus === 'unpaid') {
      return cheque.status !== 'مدفوع';
    }
    return true;
  });

  const handleExport = () => {
    exportChequesToExcel(filteredCheques);
  };

  // Display-only summary derived from current filter
  const paidCount = filteredCheques.filter((c) => c.status === 'مدفوع').length;
  const unpaidCount = filteredCheques.length - paidCount;
  const totalAmount = filteredCheques.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

  const summaryCards = [
    {
      label: 'إجمالي الشيكات',
      value: filteredCheques.length,
      icon: <ReceiptLongIcon />,
      color: '#4338CA',
      bg: '#EEF2FF',
    },
    {
      label: 'شيكات مدفوعة',
      value: paidCount,
      icon: <CheckCircleIcon />,
      color: '#16A34A',
      bg: '#DCFCE7',
    },
    {
      label: 'شيكات غير مدفوعة',
      value: unpaidCount,
      icon: <ErrorOutlineIcon />,
      color: '#DC2626',
      bg: '#FEE2E2',
    },
    {
      label: 'إجمالي المبلغ',
      value: `${totalAmount.toLocaleString('en-US')} ₪`,
      icon: <PaymentsIcon />,
      color: '#0284C7',
      bg: '#E0F2FE',
    },
  ];

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ReceiptLongIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                الشيكات غير المحصلة / الراجعة
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                متابعة وتصفية وتصدير الشيكات حسب حالة التحصيل
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            تصدير إلى Excel
          </Button>
        </MotionPaper>

        {/* Summary Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          {summaryCards.map((card, index) => (
            <MotionPaper
              key={card.label}
              component={motion.div}
              elevation={0}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.05 * index, ease: 'easeOut' }}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: '#fff',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                border: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  bgcolor: card.bg,
                  color: card.color,
                  borderRadius: 2.5,
                  width: 48,
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {card.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '1.5rem',
                    fontWeight: 800,
                    color: '#1E1B4B',
                    lineHeight: 1.2,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {card.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                >
                  {card.label}
                </Typography>
              </Box>
            </MotionPaper>
          ))}
        </Box>

        {/* خيارات التصفية والأكشنات */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
          sx={{
            mb: 3,
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <ToggleButtonGroup
            value={filterStatus}
            exclusive
            onChange={(e, newStatus) => newStatus && setFilterStatus(newStatus)}
            color="primary"
            sx={{
              gap: 1,
              '& .MuiToggleButton-root': {
                border: '1px solid #E5E7EB',
                borderRadius: '10px !important',
                textTransform: 'none',
                fontWeight: 700,
                color: '#6B7280',
                px: 2.5,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                '&.Mui-selected': {
                  bgcolor: '#4338CA',
                  color: '#fff',
                  '&:hover': { bgcolor: '#3730A3' },
                },
              },
            }}
          >
            <ToggleButton value="all">كل الشيكات</ToggleButton>
            <ToggleButton value="unpaid">الشيكات غير المدفوعة فقط</ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="outlined"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              px: 2.5,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              borderColor: '#16A34A',
              color: '#16A34A',
              '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
            }}
          >
            تصدير إلى Excel
          </Button>
        </MotionPaper>

        {/* عرض الشيكات */}
        {filteredCheques.length === 0 ? (
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            sx={{
              p: 6,
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
              textAlign: 'center',
            }}
          >
            <ReceiptLongIcon sx={{ fontSize: 48, color: '#C7CCD9', mb: 1 }} />
            <Typography
              variant="body1"
              sx={{ color: '#6B7280', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}
            >
              لا توجد شيكات حسب الفلترة المختارة.
            </Typography>
          </MotionPaper>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
            }}
            dir="rtl"
          >
            {filteredCheques.map((cheque, index) => {
              const isPaid = cheque.status === 'مدفوع';
              return (
                <MotionCard
                  key={cheque.id}
                  component={motion.div}
                  elevation={0}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.4), ease: 'easeOut' }}
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#fff',
                    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                    border: '1px solid #E5E7EB',
                    borderInlineStart: `4px solid ${isPaid ? '#16A34A' : '#DC2626'}`,
                    overflow: 'hidden',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 1.5 }}
                    >
                      <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{
                            bgcolor: '#EEF2FF',
                            color: '#4338CA',
                            borderRadius: 2,
                            width: 40,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <PersonIcon fontSize="small" />
                        </Box>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            color: '#1E1B4B',
                            fontFamily: 'Cairo, Tajawal, sans-serif',
                          }}
                        >
                          {cheque.targetName}
                        </Typography>
                      </Stack>
                      {isPaid ? (
                        <Chip
                          label="مدفوع"
                          size="small"
                          icon={<CheckCircleIcon sx={{ fontSize: 16, color: '#16A34A !important' }} />}
                          sx={{
                            bgcolor: '#DCFCE7',
                            color: '#16A34A',
                            fontWeight: 700,
                            fontFamily: 'Cairo, Tajawal, sans-serif',
                          }}
                        />
                      ) : (
                        <Chip
                          label="غير مدفوع"
                          size="small"
                          icon={<ErrorOutlineIcon sx={{ fontSize: 16, color: '#DC2626 !important' }} />}
                          sx={{
                            bgcolor: '#FEE2E2',
                            color: '#DC2626',
                            fontWeight: 700,
                            fontFamily: 'Cairo, Tajawal, sans-serif',
                          }}
                        />
                      )}
                    </Stack>

                    <Stack spacing={1.25}>
                      <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
                        <EventIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                        <Typography
                          variant="body2"
                          sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                        >
                          تاريخ الدفع:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                        >
                          {cheque.paymentDate}
                        </Typography>
                      </Stack>

                      <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
                        <PaymentsIcon sx={{ fontSize: 18, color: '#6B7280' }} />
                        <Typography
                          variant="body2"
                          sx={{ color: '#6B7280', fontFamily: 'Cairo, Tajawal, sans-serif' }}
                        >
                          المبلغ:
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: '1.05rem',
                            color: '#1E1B4B',
                            fontFamily: 'Cairo, Tajawal, sans-serif',
                          }}
                        >
                          {cheque.amount.toLocaleString('en-US')} شيكل
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </MotionCard>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UncollectedChequesPage;
